import { EventReward, parseReward as parseEventReward } from "./events";

/**
 * Achievements and tower mastery (FINISH_PLAN WP7, DESIGN §9.7), parsed from the generated bundle's `achievements`
 * section (balance/src/achievements.json). Each achievement watches one lifetime metric (meta/achievements.ts
 * ACHIEVEMENT_METRICS) and pays once when it reaches its target. Rewards are mostly Marks, resources, speed-ups and
 * chests; gems only on the late campaign clears the free-income model has room for (gen.py, achievementGems.test).
 */

/** What an achievement pays: an event-style reward plus an optional commander title (a cosmetic, never a stat). */
export interface AchievementReward extends EventReward {
  /** The commander title it unlocks ("" for none). */
  readonly title: string;
}

export interface AchievementDef {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** Sheet section: campaign, stars, combat, bosses, towers, gear, endgame. */
  readonly group: string;
  /** Lifetime metric it watches (meta/achievements.ts ACHIEVEMENT_METRICS). */
  readonly metric: string;
  readonly target: number;
  readonly reward: AchievementReward;
}

/** A tower-mastery tier: lifetime kills by one tower type that light its trim (bronze, silver, gold). */
export interface MasteryTier {
  readonly id: string;
  readonly name: string;
  readonly kills: number;
}

export interface AchievementsData {
  /** Sheet sections in display order. */
  readonly groups: readonly { readonly id: string; readonly name: string }[];
  readonly list: readonly AchievementDef[];
  /** Rising kill thresholds per tower type (Almanac trims and the `towersMastered` metric's top tier). */
  readonly mastery: readonly MasteryTier[];
}

/** No achievements data (an old bundle, a test fixture). */
export function emptyAchievements(): AchievementsData { return { groups: [], list: [], mastery: [] }; }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json): number {
  if (typeof v === "number" && Number.isInteger(v)) return v;
  throw new TypeError(`expected integer, got ${JSON.stringify(v)}`);
}
function str(v: Json): string {
  if (typeof v !== "string") throw new TypeError(`expected string, got ${JSON.stringify(v)}`);
  return v;
}
function req(o: Json, key: string): Json {
  if (o === null || typeof o !== "object" || !(key in o)) throw new Error(`Missing JSON member '${key}'`);
  return o[key];
}
function opt(o: Json, key: string, fallback: Json): Json { return o !== null && typeof o === "object" && key in o ? o[key] : fallback; }

/**
 * An achievement's reward: an event reward (the same parser, so the gear v2 forge goods — `essence`, `sigil`,
 * `sigils` — pay here too) plus its title.
 */
function parseReward(r: Json): AchievementReward {
  return { ...parseEventReward(r), title: str(opt(r, "title", "")) };
}

/** Reads the `achievements` section from the bundle root; a bundle without one gets `emptyAchievements`. */
export function parseAchievements(root: Json): AchievementsData {
  const a = opt(root, "achievements", undefined);
  if (a === undefined) return emptyAchievements();
  return {
    groups: (req(a, "groups") as Json[]).map((g) => ({ id: str(req(g, "id")), name: str(req(g, "name")) })),
    list: (req(a, "list") as Json[]).map((d) => ({
      id: str(req(d, "id")), name: str(req(d, "name")), description: str(req(d, "description")), group: str(req(d, "group")),
      metric: str(req(d, "metric")), target: int(req(d, "target")), reward: parseReward(req(d, "reward")),
    })),
    mastery: (req(a, "mastery") as Json[]).map((t) => ({ id: str(req(t, "id")), name: str(req(t, "name")), kills: int(req(t, "kills")) })),
  };
}
